import { computed, type Ref } from "vue";
import { useGridStore } from "~/stores/grid";

interface ScrollbarLimits {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function useCanvasScrollbars(
  stageSize: Ref<{ width: number; height: number }>,
  contentSize: Ref<{ width: number; height: number }>,
) {
  const store = useGridStore();
  const SAFE_MARGIN = 320;

  const limits = computed<ScrollbarLimits>(() => {
    const zoom = store.gridZoom;
    const relW = contentSize.value.width * zoom;
    const relH = contentSize.value.height * zoom;
    const sw = stageSize.value.width;
    const sh = stageSize.value.height;

    if (sw > relW && sh > relH) {
      return { minX: 0, maxX: sw - relW, minY: 0, maxY: sh - relH };
    }

    return {
      minX: -relW + SAFE_MARGIN,
      maxX: sw - SAFE_MARGIN,
      minY: -relH + SAFE_MARGIN,
      maxY: sh - SAFE_MARGIN,
    };
  });

  const visibleX = computed(() => {
    const zoom = store.gridZoom;
    return contentSize.value.width * zoom > stageSize.value.width;
  });

  const visibleY = computed(() => {
    const zoom = store.gridZoom;
    return contentSize.value.height * zoom > stageSize.value.height;
  });

  const isVisible = computed(() => visibleX.value || visibleY.value);

  const scrollableRangeX = computed(
    () => limits.value.maxX - limits.value.minX,
  );
  const scrollableRangeY = computed(
    () => limits.value.maxY - limits.value.minY,
  );

  const thumbRatioX = computed(() => {
    const zoom = store.gridZoom;
    const ratio = stageSize.value.width / (contentSize.value.width * zoom);
    return Math.max(0.05, Math.min(1, ratio));
  });

  const thumbRatioY = computed(() => {
    const zoom = store.gridZoom;
    const ratio = stageSize.value.height / (contentSize.value.height * zoom);
    return Math.max(0.05, Math.min(1, ratio));
  });

  const thumbPosX = computed(() => {
    if (scrollableRangeX.value === 0) return 0;
    return (store.gridPos.x - limits.value.minX) / scrollableRangeX.value;
  });

  const thumbPosY = computed(() => {
    if (scrollableRangeY.value === 0) return 0;
    return (store.gridPos.y - limits.value.minY) / scrollableRangeY.value;
  });

  function clampPos(x: number, y: number) {
    return {
      x: Math.min(Math.max(x, limits.value.minX), limits.value.maxX),
      y: Math.min(Math.max(y, limits.value.minY), limits.value.maxY),
    };
  }

  function setPositionFromThumbX(normalizedPos: number) {
    const clamped = Math.min(Math.max(normalizedPos, 0), 1);
    const x = limits.value.minX + clamped * scrollableRangeX.value;
    store.gridPos = clampPos(x, store.gridPos.y);
  }

  function setPositionFromThumbY(normalizedPos: number) {
    const clamped = Math.min(Math.max(normalizedPos, 0), 1);
    const y = limits.value.minY + clamped * scrollableRangeY.value;
    store.gridPos = clampPos(store.gridPos.x, y);
  }

  function panByDelta(deltaX: number, deltaY: number) {
    store.gridPos = clampPos(
      store.gridPos.x - deltaX,
      store.gridPos.y - deltaY,
    );
  }

  return {
    isVisible,
    visibleX,
    visibleY,
    thumbRatioX,
    thumbRatioY,
    thumbPosX,
    thumbPosY,
    setPositionFromThumbX,
    setPositionFromThumbY,
    panByDelta,
  };
}
